import React from 'react';
import { StyleSheet, View } from 'react-native';

import { DateInput } from '@/components/form/date-input';
import { AssociationTypeahead } from '@/components/search/association-typeahead';
import { Chip, SegmentedButtons, Text, useTheme } from '@/components/ui';
import { useAssociation } from '@/hooks/use-data';
import type { ReportDateField } from '@/services/data-service';
import { APPLICATION_STATUS_LABEL } from '@/utils/application-status';
import { businessMsToDate, formatBusinessDateIso, todayBusinessIso } from '@/utils/date-utils';
import {
	normalizeStatuses,
	presetRange,
	REPORT_PRESETS,
	type ReportPreset,
} from '@/utils/exterior-improvements-report';

export interface ReportControlsValue {
	preset: ReportPreset;
	from: string;
	to: string;
	dateField: ReportDateField;
	/** Empty = all statuses. Always sorted + deduped (see `normalizeStatuses`). */
	statuses: number[];
	associationId?: string;
}

const STATUS_OPTIONS = Object.entries(APPLICATION_STATUS_LABEL).map(([code, label]) => ({
	code: Number(code),
	label,
}));

const DATE_FIELD_BUTTONS = [
	{ value: 'submitted', label: 'Submitted', testID: 'datefield-submitted' },
	{ value: 'decided', label: 'Decided', testID: 'datefield-decided' },
];

/** A `YYYY-MM-DD` business day → the Date `DateInput` expects (UTC fields carry the day). */
const isoToInputDate = (iso: string): Date | null => {
	const ms = Date.parse(iso);
	return Number.isFinite(ms) ? businessMsToDate(ms) : null;
};

/** A cleared input falls back to ET today — never the viewer's clock. */
const inputDateToIso = (d: Date | null): string => (d ? formatBusinessDateIso(d.getTime()) : todayBusinessIso());

/**
 * Filters for the Exterior Improvements report (#644): range preset or custom dates,
 * which date the range applies to, statuses, and (portfolio page only) an association.
 */
export const ReportControls: React.FC<{
	value: ReportControlsValue;
	onChange: (v: ReportControlsValue) => void;
	showAssociationPicker?: boolean;
}> = ({ value, onChange, showAssociationPicker = false }) => {
	const theme = useTheme();
	const { data: association } = useAssociation(showAssociationPicker ? value.associationId ?? '' : '');

	const pickPreset = (preset: ReportPreset) => {
		const range = presetRange(preset);
		onChange({ ...value, preset, ...(range ?? {}) });
	};

	const toggleStatus = (code: number) => {
		const next = value.statuses.includes(code)
			? value.statuses.filter((s) => s !== code)
			: [...value.statuses, code];
		onChange({ ...value, statuses: normalizeStatuses(next) });
	};

	const label = { color: theme.colors.onSurfaceVariant };

	return (
		<View style={styles.container}>
			<View style={styles.line}>
				{REPORT_PRESETS.map(({ key, label: text }) => (
					<Chip
						key={key}
						testID={`preset-${key}`}
						selected={value.preset === key}
						onPress={() => pickPreset(key)}
						style={styles.chip}
					>
						{text}
					</Chip>
				))}
			</View>

			{value.preset === 'custom' ? (
				<View style={styles.line}>
					<Text style={[styles.label, label]}>From</Text>
					<DateInput
						label=""
						utcDay
						testID="report-from"
						value={isoToInputDate(value.from)}
						onChange={(d) => onChange({ ...value, from: inputDateToIso(d) })}
					/>
					<Text style={[styles.label, label]}>To</Text>
					<DateInput
						label=""
						utcDay
						testID="report-to"
						value={isoToInputDate(value.to)}
						onChange={(d) => onChange({ ...value, to: inputDateToIso(d) })}
					/>
				</View>
			) : null}

			<View style={styles.line}>
				<Text style={[styles.label, label]}>Date range applies to</Text>
				<SegmentedButtons
					buttons={DATE_FIELD_BUTTONS}
					value={value.dateField}
					onValueChange={(v) => onChange({ ...value, dateField: v === 'decided' ? 'decided' : 'submitted' })}
					style={styles.segmented}
				/>
			</View>

			<View style={styles.line}>
				<Text style={[styles.label, label]}>Status</Text>
				{STATUS_OPTIONS.map(({ code, label: text }) => (
					<Chip
						key={code}
						testID={`status-${code}`}
						selected={value.statuses.includes(code)}
						onPress={() => toggleStatus(code)}
						style={styles.chip}
					>
						{text}
					</Chip>
				))}
			</View>

			{showAssociationPicker ? (
				<View style={styles.picker}>
					<AssociationTypeahead
						testID="report-association"
						placeholder="All associations"
						value={value.associationId ? association ?? null : null}
						onSelect={(a) => onChange({ ...value, associationId: a?.id || undefined })}
						status="all"
					/>
				</View>
			) : null}
		</View>
	);
};

const styles = StyleSheet.create({
	container: { gap: 10, marginBottom: 12 },
	line: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
	chip: { marginRight: 0 },
	label: { fontSize: 13 },
	segmented: { maxWidth: 280 },
	picker: { maxWidth: 420 },
});
